import { useState } from "react";
import { Flex, SegmentedControl, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { InvestorToInvestorView } from "../InvestorToInvestorView";
import { useInvestorAccountsComparison } from "../InvestorToInvestorView/useInvestorAccountsComparison";
import { hasAccountsMismatch } from "../InvestorToInvestorView/utils";
import { ProfileToInvestorView } from "../ProfileToInvestorView";
import { useProfileComparison } from "../ProfileToInvestorView/useProfileComparison";
import { hasProfileDiscrepancy } from "../ProfileToInvestorView/utils";
import { ProfileTreeView } from "./ProfileTreeView";
import * as styles from "./styles_vanilla.css";

type SnapshotTab = "profile-to-account" | "compare-accounts" | "org-structure";

const WARNING_ICON_PROPS = {
  iconProps: { type: "exclamation-triangle", color: Colors.Warning600, variant: "outline" },
} as const;

export type ProfileSnapshotProps = {
  rootProfileId: number;
  arenaId: number | undefined;
  entityId: number | undefined;
  investmentAccountId: number | null;
  investmentAccountSettled: boolean;
  profileGuid: string | null;
  profileName: string | null;
  isIaAssociated: boolean;
};

export const ProfileSnapshot = ({
  rootProfileId,
  arenaId,
  entityId,
  investmentAccountId,
  investmentAccountSettled,
  profileGuid,
  profileName,
  isIaAssociated,
}: ProfileSnapshotProps) => {
  const [tab, setTab] = useState<SnapshotTab>("profile-to-account");

  const { matchData, queryResult, investmentAccountNode } = useProfileComparison({
    arenaId,
    entityId,
    investmentAccountId,
    investmentAccountSettled,
    profileId: rootProfileId,
    profileName,
    isIaAssociated,
  });

  const { compareData: investorAccountsData, queryResult: investorAccountsQueryResult } =
    useInvestorAccountsComparison({
      arenaId,
      profileGuid,
      investmentAccountId,
      currentAccountNode: investmentAccountNode,
    });

  // Comparing accounts needs both sides. The current position is only loaded for a profile
  // associated with the case's investment account, so without it there is no baseline to compare
  // the profile's other accounts against.
  const canCompareAccounts =
    investmentAccountNode !== null && (investorAccountsData?.existingAccountCount ?? 0) > 0;

  const showInvestmentAccount = matchData?.showInvestmentAccount ?? false;
  const hasDiscrepancy = hasProfileDiscrepancy(matchData);
  const accountsMismatch = hasAccountsMismatch(investorAccountsData);

  const tabOptions = [
    {
      label: showInvestmentAccount ? "Compare profile to account" : "Compliance profile",
      value: "profile-to-account",
      ...(hasDiscrepancy ? WARNING_ICON_PROPS : {}),
    },
    ...(canCompareAccounts
      ? [
          {
            label: "Compare accounts",
            value: "compare-accounts",
            ...(accountsMismatch ? WARNING_ICON_PROPS : {}),
          },
        ]
      : []),
    { label: "Organization structure", value: "org-structure" },
  ];
  // The options depend on loaded data, so the selected tab can stop being offered.
  const activeTab = tabOptions.some((option) => option.value === tab)
    ? tab
    : "profile-to-account";

  return (
    <Flex className={styles.container}>
      <Text variant="h3">Compliance profile preview</Text>
      <Text variant="secondary-md">
        Review the details below to confirm your profile selection:
      </Text>
      <SegmentedControl
        value={activeTab}
        onChange={(value) => setTab(value as SnapshotTab)}
        options={tabOptions}
        className={styles.tabHost}
      />
      {activeTab === "profile-to-account" && (
        <ProfileToInvestorView matchData={matchData} queryResult={queryResult} />
      )}
      {activeTab === "compare-accounts" && (
        <InvestorToInvestorView
          compareData={investorAccountsData}
          queryResult={investorAccountsQueryResult}
        />
      )}
      {activeTab === "org-structure" && <ProfileTreeView rootProfileId={rootProfileId} />}
    </Flex>
  );
};
